import {
  UPLOADER_STATUS_COMPLETED,
  UPLOADER_STATUS_COMPLETING,
  UPLOADER_STATUS_FAILED,
  UPLOADER_STATUS_PAUSED,
  type UploaderState,
} from '@iconik-io/uploader/src/state';
import { isStagedUpload } from '@iconik-io/uploader/src/state/upload_state';

import type { UploadItem } from './types';

export {
  getUploadedBytes,
  isStagedUpload,
} from '@iconik-io/uploader/src/state/upload_state';

/** Paused after it started, as opposed to staged. */
export const isPausedUpload = (state: UploaderState): boolean =>
  state.status === UPLOADER_STATUS_PAUSED && state.chunks !== undefined;

export const isFileExistsFailure = (
  state: UploaderState,
  item: UploadItem | undefined,
): boolean =>
  state.status === UPLOADER_STATUS_FAILED && Boolean(item?.error?.fileExists);

/** The file is uploaded and its asset can be looked at. */
export const isUploadedFile = (state: UploaderState): boolean =>
  state.status === UPLOADER_STATUS_COMPLETING ||
  state.status === UPLOADER_STATUS_COMPLETED;

/** Uploads that clearing the list takes off it. */
export const canClearUpload = (state: UploaderState): boolean =>
  isStagedUpload(state) ||
  state.status === UPLOADER_STATUS_FAILED ||
  state.status === UPLOADER_STATUS_COMPLETED;

export const isNewAssetUpload = (state: UploaderState): boolean =>
  state.target.type === 'newAsset';

export const isVersionUpload = (state: UploaderState): boolean =>
  state.target.type === 'newVersion';

export const isPlaceholderUpload = (state: UploaderState): boolean =>
  state.target.type === 'existingAsset' &&
  Boolean(state.target.fillsPlaceholder);

export const isProxyUpload = (state: UploaderState): boolean =>
  state.uploadKind.kind === 'proxy';

export const isSubtitleUpload = (state: UploaderState): boolean =>
  state.uploadKind.kind === 'file' && Boolean(state.uploadKind.subtitles);

export const canAddMetadata = (state: UploaderState): boolean =>
  isNewAssetUpload(state) || isPlaceholderUpload(state);

export const getUploadAssetId = (state: UploaderState): string | undefined =>
  state.target.assetId;

export const getUploadVersionId = (state: UploaderState): string | undefined =>
  state.target.type === 'newVersion' ? state.target.versionId : undefined;

export const getUploadSourceVersionId = (
  state: UploaderState,
): string | undefined =>
  state.target.type === 'newVersion' ? state.target.sourceVersionId : undefined;

export const getUploadFormatName = (
  state: UploaderState,
): string | undefined =>
  state.uploadKind.kind === 'file' ? state.uploadKind.formatName : undefined;

/** Name of the picked file, whatever path the upload puts it under. */
export const getUploadFileName = (state: UploaderState): string =>
  state.fileInfo.targetPath.split('/').pop() || state.fileInfo.targetPath;

export const getUploadTitle = (state: UploaderState): string =>
  state.options.customFileName ??
  state.options.customAssetTitle ??
  getUploadFileName(state);
